import { AlertTriangle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  formatMismatchFieldLine,
  getDisplayableMismatchFields,
  getIdentityMismatchSeverity,
} from '@/lib/identityMismatch';
import type { IdentityMismatch } from '@/types';

interface Props {
  mismatch?: IdentityMismatch | null;
  /** Table rows: icon only. Detail header: icon + label (default). */
  variant?: 'badge' | 'icon';
  className?: string;
}

export default function IdentityMismatchBadge({
  mismatch,
  variant = 'badge',
  className,
}: Props) {
  const { t } = useLanguage();

  if (!mismatch?.hasMismatch) return null;

  const displayFields = getDisplayableMismatchFields(mismatch.fields);
  const severity = mismatch ? getIdentityMismatchSeverity(mismatch) : null;
  if (displayFields.length === 0 || !severity) return null;

  const isCritical = severity === 'critical';
  const Icon = isCritical ? AlertTriangle : Info;
  const tooltipLines = displayFields.map((field) => formatMismatchFieldLine(field, t));
  const label = t(
    isCritical
      ? 'application.identityMismatch.badge'
      : 'application.identityMismatch.badgeContact',
  );
  const tooltipTitle = t(
    isCritical
      ? 'application.identityMismatch.tooltipTitle'
      : 'application.identityMismatch.tooltipTitleContact',
  );
  const iconOnly = variant === 'icon';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center justify-center shrink-0 rounded-md border',
            iconOnly ? 'h-6 w-6' : 'gap-1 px-2 py-0.5 text-xs font-medium whitespace-nowrap',
            isCritical
              ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : 'border-blue-500/40 bg-blue-500/10 text-blue-800 dark:text-blue-200',
            className,
          )}
          aria-label={iconOnly ? label : undefined}
          title={iconOnly ? undefined : label}
        >
          <Icon className={cn('shrink-0', iconOnly ? 'h-3.5 w-3.5' : 'h-3 w-3')} />
          {!iconOnly && label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <p className="text-xs font-medium mb-1">{tooltipTitle}</p>
        <ul className="space-y-0.5 text-xs">
          {tooltipLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
